import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import * as React from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useMiaRouteMatch, useMiaSelector } from '../components/hooks';
import { UploadBanner } from '../components/uploadComponents/UploadBanner';
import { classMerge } from '../utils/styles/classMerge';
import { FullPageLoading } from './FullPageLoading';
import { GlobalDialogs } from './GlobalDialogs';
import { Header } from './Header';
import { DRAWER_WIDTH } from './layoutConstants';
import { LeftNav } from './LeftNav';
import { MainContent } from './MainContent';
import { RightSidebar } from './RightSidebar';
import { SidebarContextProvider } from './SidebarContext';

interface IMainPageLayoutProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appFrame: {
      display: 'flex',
      height: '100%',
      position: 'relative',
      width: '100%'
    },
    container: {
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      height: 'calc(100vh - 56px)',
      [theme.breakpoints.up('sm')]: {
        height: 'calc(100vh - 64px)'
      }
    },
    contentWrapper: {
      marginTop: 64,
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: `calc(100vw - ${DRAWER_WIDTH}px)`
      }
    },
    hide: {
      display: 'none'
    },
    main: {
      backgroundColor: theme.palette.background.default,
      height: 'calc(100vh - 56px)',
      overflow: 'auto',
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        height: 'calc(100vh - 64px)',
        padding: theme.spacing(3)
      }
    },
    mainContent: {
      flex: 2,
      overflow: 'auto',
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3)
      }
    },
    root: {
      height: 'calc(100vh - 4px)',
      overflow: 'hidden',
      width: '100%',
      zIndex: 1
    },
    sidebar: {
      width: DRAWER_WIDTH
    },
    wrapper: {
      height: '100%'
    }
  })
);

let MainPageLayout: React.ComponentType<IMainPageLayoutProps> = ({}) => {
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const showFullPageLoading = useMiaSelector(state => state.ui.showFullPageLoading);
  const matches = useMiaRouteMatch();

  const handleDrawerToggle = React.useCallback(() => setMobileOpen(current => !current), []);

  return (
    <div className={classes.root}>
      <div className={classes.appFrame}>
        <SidebarContextProvider>
          <Header matchingRoutes={matches} onDrawerClick={handleDrawerToggle} />
          <LeftNav matches={matches} isOpen={mobileOpen} onClose={handleDrawerToggle} />
          <div className={classes.contentWrapper}>
            <UploadBanner />
            <div className={classes.container}>
              <div className={classes.mainContent}>
                <React.Suspense fallback={<FullPageLoading />}>
                  <ErrorBoundary>
                    {showFullPageLoading && <FullPageLoading />}
                    <div
                      id="main-content-loading-wrapper"
                      className={classMerge(classes.wrapper, showFullPageLoading ? classes.hide : '')}
                    >
                      <MainContent />
                    </div>
                  </ErrorBoundary>
                </React.Suspense>
              </div>
              <RightSidebar matchingRoutes={matches} />
            </div>
          </div>
        </SidebarContextProvider>
      </div>
      <GlobalDialogs />
    </div>
  );
};

MainPageLayout = React.memo(MainPageLayout);
export { MainPageLayout };
